import React, { useCallback, useMemo } from 'react';
import deepEqual from 'fast-deep-equal/es6/react';
import { useSelector } from 'react-redux';
import { getIDFromUrl } from '@rossum/api-client';
import { SchemaFieldIconType } from '@rossum/components/schema-field-icon/SchemaFieldIcon';
import { ListItemProps, Skeleton, Stack } from '@rossum/ui/material';
import { sidebarFieldIdsEnabledSelector } from '../../../../redux/modules/user/selectors';
import { Annotation } from '../../../../types/annotation';
import { SidebarItemLabel, SidebarItemLayout } from '../../../document-sidebar';
import { SidebarSimplevalueFieldModel } from '../useSidebarData';
import { InactiveValue } from './shared/InactiveValue';
import { isMemoryValueSource } from './shared/isMemoryValueSource';
import { ItemContextActions } from './shared/ItemContextActions';
import { MagicContainer } from './shared/MagicContainer';
import { MessageIndicator } from './shared/MessageIndicator';
import { Messages } from './shared/Messages';
import { SidebarItemActiveValue } from './shared/SidebarItemActiveValue';
import { formatConfidenceScore, getFieldsSettingsPath } from './utils';

type SimplevalueItemProps = Omit<ListItemProps, 'onChange' | 'onClick'> & {
  sidebarScrollableRef: HTMLDivElement | null;
  annotation: Annotation | undefined;
  item: SidebarSimplevalueFieldModel;
  active: boolean;
  selected?: boolean;
  disabled: boolean;
  loading: boolean;
  ghost?: boolean;
  onChange: (item: SidebarSimplevalueFieldModel, newValue: string) => void;
  onClick: (datapointId: number) => void;
};

const SimpleValueItem = React.memo(
  ({
    sidebarScrollableRef,
    annotation,
    item,
    active,
    selected,
    disabled,
    onChange,
    onClick,
    loading,
    ghost,
    ...listItemProps
  }: SimplevalueItemProps) => {
    const handleChange = useCallback(
      (newValue: string) => {
        onChange(item, newValue);
      },
      [item, onChange]
    );

    const handleClick = useCallback(() => {
      onClick(item.id);
    }, [item.id, onClick]);

    const isEdited = !!('noRecalculation' in item && item.noRecalculation);

    const isConfirmed = useMemo(() => {
      return item.validationSources.includes('human');
    }, [item.validationSources]);

    const iconType: SchemaFieldIconType = item.valueSource;

    const isMemory = isMemoryValueSource(item.struct);

    const iconVariant = useMemo(() => {
      if (isEdited) {
        return 'edited';
      }

      if (isMemory) {
        return 'saved';
      }

      if (isConfirmed) {
        return 'confirmed';
      }

      return 'default';
    }, [isConfirmed, isEdited, isMemory]);

    const iconColor = useMemo(() => {
      if (isMemory) {
        return 'aurora' as const;
      }

      // isEdited is always also isConfirmed because we set `human` validation source but we might want to change it in the future :shrug:
      if (isEdited || isConfirmed) {
        return 'success' as const;
      }

      return 'inherit' as const;
    }, [isConfirmed, isEdited, isMemory]);

    const isDisabled = disabled || item.editing === 'disabled';

    const sidebarAdditionalInfo = useSelector(sidebarFieldIdsEnabledSelector);

    const queueId = annotation ? getIDFromUrl(annotation?.queue) : undefined;

    return (
      <MagicContainer
        active={active}
        selected={selected}
        disabled={isDisabled}
        ghost={ghost}
        sidebarScrollableRef={sidebarScrollableRef}
        onClick={active ? undefined : handleClick}
        {...listItemProps}
      >
        <SidebarItemLayout
          iconSlot={
            <ItemContextActions
              item={item}
              active={active}
              disabled={isDisabled}
              iconProps={{
                type: iconType,
                variant: iconVariant,
                color: iconColor,
              }}
              fieldSettingsPath={
                queueId && item.schemaId
                  ? getFieldsSettingsPath(queueId, item.schemaId)
                  : null
              }
            />
          }
          labelSlot={
            <SidebarItemLabel
              label={item.label}
              sublabel={sidebarAdditionalInfo ? item.schemaId : undefined}
              isRequired={item.required}
            />
          }
          valueSlot={
            loading ? (
              <Stack height={1} justifyContent="center">
                <Skeleton height="100%" />
              </Stack>
            ) : active ? (
              <SidebarItemActiveValue
                value={item.value}
                onChange={handleChange}
                disabled={isDisabled}
              />
            ) : (
              <InactiveValue
                value={item.value}
                confidence={formatConfidenceScore(item.confidenceScore)}
              />
            )
          }
          decorationSlot={
            active || !item.messagesStats ? null : (
              <MessageIndicator messagesStats={item.messagesStats} />
            )
          }
        />
        {active
          ? item.messages.length > 0 && <Messages messages={item.messages} />
          : null}
      </MagicContainer>
    );
  },
  (prev, next) => {
    return deepEqual(prev, next);
  }
);

SimpleValueItem.displayName = 'SimpleValueItem';

export { SimpleValueItem };
